import { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Menu,
  Fab,
  Stack,
  Alert,
} from '@mui/material'
import {
  Add,
  AccountBalance,
  Payment,
  MoreVert,
  Edit,
  Delete,
  Calculate,
  Receipt,
} from '@mui/icons-material'
import { format } from 'date-fns'

import { 
  Loan, 
  LoanFormData, 
  LoanPayment, 
  LoanSummary, 
  LoanType, 
  EMICalculation 
} from '../../types'
import { loanService } from '../../services/loanService'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import ConfirmDialog from '../../components/Common/ConfirmDialog'
import { useAuth } from '../../contexts/AuthContext'

const LoansPage = () => {
  const { user } = useAuth()
  const [loans, setLoans] = useState<Loan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Form states
  const [loanFormOpen, setLoanFormOpen] = useState(false)
  const [paymentFormOpen, setPaymentFormOpen] = useState(false)
  const [calculatorOpen, setCalculatorOpen] = useState(false)
  const [editingLoan, setEditingLoan] = useState<Loan | null>(null)
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null)

  // Form data
  const [loanFormData, setLoanFormData] = useState<LoanFormData>({
    name: '',
    principalAmount: 0,
    interestRate: 0,
    tenureMonths: 0,
    startDate: format(new Date(), 'yyyy-MM-dd'),
    loanType: 'personal',
    createReminder: true,
  })

  const [paymentFormData, setPaymentFormData] = useState({
    amount: 0,
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    notes: '',
  })

  const [calculatorData, setCalculatorData] = useState({
    principalAmount: 0,
    interestRate: 0,
    tenureMonths: 0,
  })

  const [calculatorResult, setCalculatorResult] = useState<EMICalculation | null>(null)

  // Data
  const [loanTypes, setLoanTypes] = useState<LoanType[]>([])
  const [loanSummary, setLoanSummary] = useState<LoanSummary | null>(null)
  const [payments, setPayments] = useState<LoanPayment[]>([])
  const [dashboard, setDashboard] = useState<any>(null)

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuLoan, setMenuLoan] = useState<Loan | null>(null)

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [loanToDelete, setLoanToDelete] = useState<Loan | null>(null)

  useEffect(() => {
    // Only load data if user is authenticated
    if (user && localStorage.getItem('auth_token')) {
      console.log('LoansPage: User authenticated, loading data')
      loadLoans()
      loadLoanTypes()
      loadDashboard()
    } else {
      console.log('LoansPage: User not authenticated, skipping data load')
    }
  }, [user])

  const loadLoans = async () => {
    try {
      setLoading(true)
      const result = await loanService.getLoans()
      setLoans(result.data || []) // Ensure it's always an array
      setError(null)
    } catch (err) {
      setError('Failed to load loans')
      setLoans([]) // Ensure loans is always an array even on error
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const loadLoanTypes = async () => {
    try {
      const types = await loanService.getLoanTypes()
      setLoanTypes(types)
    } catch (err) {
      console.error('Failed to load loan types:', err)
    }
  }

  const loadDashboard = async () => {
    try {
      const data = await loanService.getDashboard()
      setDashboard(data)
    } catch (err) {
      console.error('Failed to load dashboard:', err)
    }
  }

  const loadLoanSummary = async (loanId: number) => {
    try {
      const summary = await loanService.getLoanSummary(loanId)
      setLoanSummary(summary)
    } catch (err) {
      console.error('Failed to load loan summary:', err)
    }
  }

  const loadPayments = async (loanId: number) => {
    try {
      const result = await loanService.getPayments(loanId)
      setPayments(result.data)
    } catch (err) {
      console.error('Failed to load payments:', err)
    }
  }

  const handleCreateLoan = async () => {
    try {
      await loanService.createLoan(loanFormData)
      setLoanFormOpen(false)
      resetLoanForm()
      loadLoans()
      loadDashboard()
    } catch (err) {
      console.error('Failed to create loan:', err)
    }
  }

  const handleUpdateLoan = async () => {
    if (!editingLoan) return

    try {
      await loanService.updateLoan(editingLoan.id, loanFormData)
      setLoanFormOpen(false)
      setEditingLoan(null)
      resetLoanForm()
      loadLoans()
    } catch (err) {
      console.error('Failed to update loan:', err)
    }
  }

  const handleDeleteLoan = async () => {
    if (!loanToDelete) return

    try {
      await loanService.deleteLoan(loanToDelete.id)
      setDeleteConfirmOpen(false)
      setLoanToDelete(null)
      loadLoans()
      loadDashboard()
    } catch (err) {
      console.error('Failed to delete loan:', err)
    }
  }

  const handleRecordPayment = async () => {
    if (!selectedLoan) return

    try {
      await loanService.recordPayment(selectedLoan.id, paymentFormData)
      setPaymentFormOpen(false)
      resetPaymentForm()
      loadLoans()
      loadDashboard()
      if (loanSummary) {
        loadLoanSummary(selectedLoan.id)
      }
      if (payments.length > 0) {
        loadPayments(selectedLoan.id)
      }
    } catch (err) {
      console.error('Failed to record payment:', err)
    }
  }

  const handleCalculateEMI = async () => {
    try {
      const result = await loanService.calculateEMI({
        principal_amount: calculatorData.principalAmount,
        interest_rate: calculatorData.interestRate,
        tenure_months: calculatorData.tenureMonths,
      })
      setCalculatorResult(result)
    } catch (err) {
      console.error('Failed to calculate EMI:', err)
    }
  }

  const resetLoanForm = () => {
    setLoanFormData({
      name: '',
      principalAmount: 0,
      interestRate: 0,
      tenureMonths: 0,
      startDate: format(new Date(), 'yyyy-MM-dd'),
      loanType: 'personal',
      createReminder: true,
    })
  }

  const resetPaymentForm = () => {
    setPaymentFormData({
      amount: 0,
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      notes: '',
    })
  }

  const openEditForm = (loan: Loan) => {
    setEditingLoan(loan)
    setLoanFormData({
      name: loan.name,
      principalAmount: loan.principalAmount,
      interestRate: loan.interestRate,
      tenureMonths: loan.tenureMonths,
      startDate: loan.startDate,
      loanType: loan.loanType,
    })
    setLoanFormOpen(true)
    setMenuAnchor(null)
  }

  const openDeleteConfirm = (loan: Loan) => {
    setLoanToDelete(loan)
    setDeleteConfirmOpen(true)
    setMenuAnchor(null)
  }

  const openPaymentForm = (loan: Loan) => {
    setSelectedLoan(loan)
    setPaymentFormData({
      ...paymentFormData,
      amount: loan.emiAmount,
    })
    setPaymentFormOpen(true)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'completed':
        return 'info'
      case 'closed':
        return 'default'
      default:
        return 'default'
    }
  }

  const getLoanTypeLabel = (type: string) => {
    const loanType = loanTypes.find(t => t.value === type)
    return loanType ? loanType.label : type
  }

  if (loading) {
    return <LoadingSpinner message="Loading loans..." />
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Loans & EMI
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Calculate />}
            onClick={() => setCalculatorOpen(true)}
          >
            EMI Calculator
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setLoanFormOpen(true)}
          >
            Add Loan
          </Button>
        </Stack>
      </Box>

      {error && (
        <ErrorMessage
          message={error}
          onRetry={loadLoans}
        />
      )}

      {/* Dashboard Summary */}
      {dashboard && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Loans
                </Typography>
                <Typography variant="h4">
                  {dashboard.summary.totalLoans}
                </Typography>
                <Typography variant="body2" color="success.main">
                  {dashboard.summary.activeLoans} active
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Principal
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(dashboard.summary.totalPrincipal)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Monthly EMI
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(dashboard.summary.totalMonthlyEMI)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Remaining Amount
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(dashboard.summary.totalRemainingAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Loans Grid */}
      <Grid container spacing={3}>
        {loans.map((loan) => (
          <Grid item xs={12} md={6} lg={4} key={loan.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" component="h2">
                      {loan.name}
                    </Typography>
                    <Typography color="textSecondary">
                      {getLoanTypeLabel(loan.loanType)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={loan.status}
                      color={getStatusColor(loan.status) as any}
                      size="small"
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setMenuLoan(loan)
                        setMenuAnchor(e.currentTarget)
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Principal Amount
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(loan.principalAmount)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    EMI Amount
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(loan.emiAmount)}
                  </Typography>
                </Box>

                {loan.calculated && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        Progress
                      </Typography>
                      <Typography variant="body2">
                        {loan.calculated.completionPercentage.toFixed(1)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={loan.calculated.completionPercentage}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        Paid: {formatCurrency(loan.paidAmount)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Remaining: {formatCurrency(loan.calculated.remainingAmount)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Interest Rate
                  </Typography>
                  <Typography variant="body2">
                    {loan.interestRate}% p.a.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Tenure
                  </Typography>
                  <Typography variant="body2">
                    {loan.tenureMonths} months
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    Start Date
                  </Typography>
                  <Typography variant="body2">
                    {format(new Date(loan.startDate), 'MMM dd, yyyy')}
                  </Typography>
                </Box>
              </CardContent>

              <CardActions>
                <Button
                  size="small"
                  startIcon={<Payment />}
                  onClick={() => openPaymentForm(loan)}
                  disabled={loan.status !== 'active'}
                >
                  Record Payment
                </Button>
                <Button
                  size="small"
                  startIcon={<Receipt />}
                  onClick={() => {
                    setSelectedLoan(loan)
                    loadLoanSummary(loan.id)
                  }}
                >
                  Details
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {loans.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <AccountBalance sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No loans found
          </Typography>
          <Typography color="textSecondary" sx={{ mb: 3 }}>
            Start by adding your first loan to track EMI payments and progress.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setLoanFormOpen(true)}
          >
            Add Your First Loan
          </Button>
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => menuLoan && openEditForm(menuLoan)}>
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => menuLoan && openDeleteConfirm(menuLoan)}>
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Loan Form Dialog */}
      <Dialog open={loanFormOpen} onClose={() => setLoanFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLoan ? 'Edit Loan' : 'Add Loan'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Loan Name"
                value={loanFormData.name}
                onChange={(e) => setLoanFormData({ ...loanFormData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Principal Amount"
                type="number"
                value={loanFormData.principalAmount}
                onChange={(e) => setLoanFormData({ ...loanFormData, principalAmount: Number(e.target.value) })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Interest Rate"
                type="number"
                value={loanFormData.interestRate}
                onChange={(e) => setLoanFormData({ ...loanFormData, interestRate: Number(e.target.value) })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">% p.a.</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tenure"
                type="number"
                value={loanFormData.tenureMonths}
                onChange={(e) => setLoanFormData({ ...loanFormData, tenureMonths: Number(e.target.value) })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">months</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={loanFormData.startDate}
                onChange={(e) => setLoanFormData({ ...loanFormData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Loan Type</InputLabel>
                <Select
                  value={loanFormData.loanType}
                  onChange={(e) => setLoanFormData({ ...loanFormData, loanType: e.target.value as any })}
                  label="Loan Type"
                >
                  {loanTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoanFormOpen(false)}>Cancel</Button>
          <Button
            onClick={editingLoan ? handleUpdateLoan : handleCreateLoan}
            variant="contained"
          >
            {editingLoan ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Form Dialog */}
      <Dialog open={paymentFormOpen} onClose={() => setPaymentFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {selectedLoan && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info">
                Recording payment for: <strong>{selectedLoan.name}</strong>
              </Alert>
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Amount"
                type="number"
                value={paymentFormData.amount}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: Number(e.target.value) })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                value={paymentFormData.paymentDate}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, paymentDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={paymentFormData.notes}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentFormOpen(false)}>Cancel</Button>
          <Button onClick={handleRecordPayment} variant="contained">
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* EMI Calculator Dialog */}
      <Dialog open={calculatorOpen} onClose={() => setCalculatorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>EMI Calculator</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Loan Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Principal Amount"
                    type="number"
                    value={calculatorData.principalAmount}
                    onChange={(e) => setCalculatorData({ ...calculatorData, principalAmount: Number(e.target.value) })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Interest Rate"
                    type="number"
                    value={calculatorData.interestRate}
                    onChange={(e) => setCalculatorData({ ...calculatorData, interestRate: Number(e.target.value) })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">% p.a.</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tenure"
                    type="number"
                    value={calculatorData.tenureMonths}
                    onChange={(e) => setCalculatorData({ ...calculatorData, tenureMonths: Number(e.target.value) })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">months</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCalculateEMI}
                    disabled={!calculatorData.principalAmount || !calculatorData.interestRate || !calculatorData.tenureMonths}
                  >
                    Calculate EMI
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Results
              </Typography>
              {calculatorResult ? (
                <Card>
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      <Typography color="textSecondary">
                        Monthly EMI
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {formatCurrency(calculatorResult.emiAmount)}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography color="textSecondary">
                        Total Amount Payable
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(calculatorResult.totalAmount)}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography color="textSecondary">
                        Total Interest
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(calculatorResult.totalInterest)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Calculate sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography color="textSecondary">
                    Enter loan details and click calculate to see EMI breakdown
                  </Typography>
                </Paper>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalculatorOpen(false)}>Close</Button>
          {calculatorResult && (
            <Button
              onClick={() => {
                setLoanFormData({
                  ...loanFormData,
                  principalAmount: calculatorData.principalAmount,
                  interestRate: calculatorData.interestRate,
                  tenureMonths: calculatorData.tenureMonths,
                })
                setCalculatorOpen(false)
                setLoanFormOpen(true)
              }}
              variant="contained"
            >
              Create Loan
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Loan"
        message={`Are you sure you want to delete "${loanToDelete?.name}"? This will also delete all associated payments.`}
        onConfirm={handleDeleteLoan}
        onCancel={() => setDeleteConfirmOpen(false)}
        severity="error"
      />

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', md: 'none' },
        }}
        onClick={() => setLoanFormOpen(true)}
      >
        <Add />
      </Fab>
    </Box>
  )
}

export default LoansPage